import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Mail,
  Send,
  Loader2,
  Handshake,
  MessageSquare,
  Megaphone,
  HelpCircle,
  CheckCircle,
  ExternalLink,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_EMAIL = 'support@automat-io.tech';
const PARTNERS_EMAIL = 'info@automat-io.tech';

const REQUEST_TYPES = [
  { value: 'general', icon: MessageSquare, color: 'text-blue-600', bg: 'bg-blue-50' },
  { value: 'partnership', icon: Handshake, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { value: 'banner', icon: Megaphone, color: 'text-orange-600', bg: 'bg-orange-50' },
  { value: 'support', icon: HelpCircle, color: 'text-purple-600', bg: 'bg-purple-50' },
];

export const ContactSection = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    request_type: 'general',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.email || form.message.length < 10) {
      setError(t('contact_error_required'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({ name: '', email: '', subject: '', message: '', request_type: 'general' });
    } catch (err) {
      setError(err.response?.data?.detail || t('contact_error_generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-20 md:py-28 bg-gradient-to-br from-slate-50 via-white to-orange-50/30"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <span className="text-xs font-mono uppercase tracking-widest text-orange-600 mb-4 block font-semibold">
            {t('contact_badge')}
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {t('contact_title')}
          </h2>
          <p className="text-lg text-slate-600">{t('contact_subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* LEFT — Direct contacts + Partners card */}
          <div className="lg:col-span-2 space-y-6">
            {/* Generic contact card */}
            <Card className="border border-slate-200 rounded-2xl bg-white hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3
                      className="text-lg font-bold text-slate-900 mb-1"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      {t('contact_direct_title')}
                    </h3>
                    <p className="text-sm text-slate-600 mb-3">{t('contact_direct_desc')}</p>
                    <a
                      href={`mailto:${CONTACT_EMAIL}`}
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold text-sm break-all"
                      data-testid="contact-direct-email"
                    >
                      {CONTACT_EMAIL}
                      <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Partners card (highlighted) */}
            <Card className="border-2 border-orange-300 rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50 hover:shadow-xl transition-all">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-orange-500/30">
                    <Handshake className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-orange-700 font-semibold mb-1 block">
                      {t('contact_partners_badge')}
                    </span>
                    <h3
                      className="text-lg font-bold text-slate-900 mb-2"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      {t('contact_partners_title')}
                    </h3>
                    <p className="text-sm text-slate-700 mb-3 leading-relaxed">
                      {t('contact_partners_desc')}
                    </p>
                    <ul className="space-y-1.5 mb-4 text-sm text-slate-700">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
                        {t('contact_partners_benefit_1')}
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
                        {t('contact_partners_benefit_2')}
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
                        {t('contact_partners_benefit_3')}
                      </li>
                    </ul>
                    <a
                      href={`mailto:${PARTNERS_EMAIL}?subject=Partnership%20AUTOMAT.IO`}
                      className="inline-flex items-center gap-2 text-orange-700 hover:text-orange-800 font-semibold text-sm break-all"
                      data-testid="contact-partners-email"
                    >
                      {PARTNERS_EMAIL}
                      <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT — Form */}
          <div className="lg:col-span-3">
            <Card className="border border-slate-200 rounded-2xl bg-white shadow-sm">
              <CardContent className="p-6 md:p-8">
                {success ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-8 w-8 text-emerald-600" />
                    </div>
                    <h3
                      className="text-2xl font-bold text-slate-900 mb-2"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      {t('contact_success_title')}
                    </h3>
                    <p className="text-slate-600 mb-6">{t('contact_success_desc')}</p>
                    <Button
                      variant="outline"
                      onClick={() => setSuccess(false)}
                      data-testid="contact-send-another"
                    >
                      {t('contact_send_another')}
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-3 block">
                        {t('contact_type_label')}
                      </Label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {REQUEST_TYPES.map((type) => {
                          const Icon = type.icon;
                          const selected = form.request_type === type.value;
                          return (
                            <button
                              type="button"
                              key={type.value}
                              onClick={() => handleChange('request_type', type.value)}
                              className={`p-3 rounded-lg border-2 transition-all text-left ${
                                selected
                                  ? 'border-orange-500 bg-orange-50'
                                  : 'border-slate-200 bg-white hover:border-slate-300'
                              }`}
                              data-testid={`contact-type-${type.value}`}
                            >
                              <div
                                className={`w-8 h-8 ${type.bg} rounded-md flex items-center justify-center mb-2`}
                              >
                                <Icon className={`h-4 w-4 ${type.color}`} />
                              </div>
                              <p className="text-xs font-semibold text-slate-900 leading-tight">
                                {t(`contact_type_${type.value}`)}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="contact-name">{t('contact_name')} *</Label>
                        <Input
                          id="contact-name"
                          value={form.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          placeholder={t('contact_name_placeholder')}
                          required
                          data-testid="contact-input-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact-email">{t('contact_email')} *</Label>
                        <Input
                          id="contact-email"
                          type="email"
                          value={form.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          placeholder="you@email.com"
                          required
                          data-testid="contact-input-email"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact-subject">{t('contact_subject')}</Label>
                      <Input
                        id="contact-subject"
                        value={form.subject}
                        onChange={(e) => handleChange('subject', e.target.value)}
                        placeholder={t('contact_subject_placeholder')}
                        data-testid="contact-input-subject"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact-message">{t('contact_message')} *</Label>
                      <textarea
                        id="contact-message"
                        value={form.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        placeholder={t('contact_message_placeholder')}
                        className="w-full rounded-md border border-slate-200 p-3 text-sm min-h-[140px] focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        required
                        maxLength={3000}
                        data-testid="contact-input-message"
                      />
                      <p className="text-xs text-slate-500 text-right">
                        {form.message.length}/3000
                      </p>
                    </div>

                    {error && (
                      <div
                        className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3"
                        data-testid="contact-error"
                      >
                        {error}
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-md py-6 font-semibold shadow-lg shadow-orange-500/20"
                      data-testid="contact-submit"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          {t('contact_sending')}
                        </>
                      ) : (
                        <>
                          {t('contact_send')}
                          <Send className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
